import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectAutoSelectNextWell,
  selectScoreGroups,
} from 'redux/selectors/StateSelectors';
import TreeCommandCard from 'components/card/TreeCommandCard';
import KeyStroke from '../KeyStroke';
import { isNullOrUndefined, GetScoreDefn } from 'utils/Global';
import EScoreType from 'enums/EScoreType';
import EScoreGroupName from 'enums/EScoreGroupName';
import './DefaultScores.scss';
import { DropDetails } from 'components/well/constants';
import {
  PreferenesDTO,
  ScoreDefinitionDTO,
  StringPropertyDTO,
} from 'business/models/Models';
import { useManualScoreGroups } from 'redux/hooks/common';
import {
  ScoresOfWellDrops,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import {
  setAutoSelectNextWell,
  setHeaderScoresGroupTypes,
} from 'redux/reducers/headerReducer';
import { FmlxSwitch } from 'fmlx-common-ui';
import RMMultiSelectSearchDropdown from 'components/customComponents/CommonComponents/RMMultiSelectSearchDropdown';
import SideBarComponent from '../SideBarComponent/SideBarComponent';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { Preference } from 'components/main/GlobalPrefrences/constants';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import {
  formatSelectedScoreGroupIds,
  getScoreIdsForSelectedGroups,
  getSelectedScoreGroupIds,
  getValidScoreGroupIds,
} from './utils';

interface Props {
  allScoresOptionsList: any[];
  selectedWellsScores: ScoresOfWellDrops[][];
  selectedWellDetails: Array<WellNavigationDetails | DropDetails>;
  applyScoring: (wellDropScoreId, wellDropScoreGroupId) => void;
  imageBatchId?: number;
  dropNumber?: number;
  cpId?: number;
}
function DefaultScores(props: Props) {
  const {
    allScoresOptionsList,
    selectedWellsScores,
    selectedWellDetails,
    applyScoring,
    imageBatchId,
    dropNumber,
    cpId,
  } = props;
  const wdScores: ScoreDefinitionDTO[] = GetScoreDefn();
  const [ScoreDefinitions, setScoreDefinitions] = useState(wdScores);
  const autoSelectNextWell = useRMSelector(selectAutoSelectNextWell);
  const [nodeMenuDetails, setNodeManuDetails] = useState([]);
  const [WellDropScoresView, setWellDropScoresView] = useState([]);
  const scoresOptionsList = useManualScoreGroups();
  const dispatch = useRMDispatch();
  const scoreGroups = useRMSelector((state) => selectScoreGroups(state));
  const selectedScoreGroupIds = useMemo(
    () => getSelectedScoreGroupIds(allScoresOptionsList, scoreGroups, wdScores),
    [allScoresOptionsList, scoreGroups, wdScores],
  );

  const saveSelectedScoreGroupPreference = useCallback(
    async (selectedGroupIds: number[]) => {
      const selectedCanvasScoreGroupsProperty = new StringPropertyDTO();
      selectedCanvasScoreGroupsProperty.name =
        ECanvasPreferencesKeys.SelectedCanvasScoreGroups;
      selectedCanvasScoreGroupsProperty.value =
        formatSelectedScoreGroupIds(selectedGroupIds);

      const preferences = new PreferenesDTO();
      preferences.baseProperties = [selectedCanvasScoreGroupsProperty];
      preferences.preferenceType = Preference.UserPreference;

      try {
        await new GlobalPreferencesService().setAllProperties(preferences);
      } catch (error) {
        console.error('Failed to save selected canvas score groups preference.', error);
      }
    },
    [],
  );

  const handleScoreGroupSelectionChange = useCallback(
    (selectedGroupIds: number[]) => {
      const validSelectedGroupIds = getValidScoreGroupIds(selectedGroupIds, wdScores);
      dispatch(
        setHeaderScoresGroupTypes(
          getScoreIdsForSelectedGroups(validSelectedGroupIds, wdScores),
        ),
      );
      saveSelectedScoreGroupPreference(validSelectedGroupIds);
    },
    [dispatch, saveSelectedScoreGroupPreference, wdScores],
  );

  const firstWell = selectedWellDetails?.[0];

  const getWellImageBatchId = (well: WellNavigationDetails | DropDetails) => {
    if ('imageBatchId' in well && !isNullOrUndefined(well.imageBatchId)) {
      return well.imageBatchId;
    }
    return imageBatchId;
  };

  const getWellDropNumber = (well: WellNavigationDetails | DropDetails) => {
    if ('dropNumber' in well && !isNullOrUndefined(well.dropNumber)) {
      return well.dropNumber;
    }
    return dropNumber;
  };

  const hasScorableWell = selectedWellDetails?.some(
    (well) => !!getWellImageBatchId(well),
  );

  const customBoxStyle = {
    height: '10vh',
    width: '5vw',
    minHeight: '70px',
    minWidth: '70px',
  };

  const customTextStyle = {
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    textAlign: 'center',
    padding: '0.75rem',
    letterSpacing: 'normal',
  };

  function scoreWellDrop(wellDropScoreId, wellDropScoreGroupId) {
    if (!hasScorableWell) {
      return;
    }
    applyScoring(wellDropScoreId, wellDropScoreGroupId);
  }

  useEffect(() => {
    if (!isNullOrUndefined(wdScores)) {
      setScoreDefinitions(wdScores);
    }
  }, [wdScores]);

  const isScoreActive = (wellDropScoreId: number, wellDropScoreGroupId: number) => {
    if (!selectedWellsScores?.length || !selectedWellDetails?.length) {
      return false;
    }
    return selectedWellsScores
      .flatMap((scores) =>
        scores.filter((s) =>
          selectedWellDetails.some((well) => {
            const wellImageBatchId = getWellImageBatchId(well);
            const wellDropNumber = getWellDropNumber(well);
            return (
              s.scoreBatchId === wellImageBatchId &&
              s.dropNumber === wellDropNumber &&
              s.wellNumber === well.wellNum &&
              (isNullOrUndefined(s.plateId) || s.plateId === well.plateID)
            );
          }),
        ),
      )
      .some(
        (score) =>
          score.wellDropScoreGroupId === wellDropScoreGroupId &&
          score.scoreType !== EScoreType.Inherited &&
          score.wellDropScoreId === wellDropScoreId,
      );
  };
  useEffect(() => {
    if (isNullOrUndefined(ScoreDefinitions)) {
      return;
    }
    const ibWellScores = ScoreDefinitions.filter(
      (scores) => scores.isAutoScore === false,
    );
    setWellDropScoresView(ibWellScores);
  }, [scoreGroups, ScoreDefinitions]);

  useEffect(() => {
    setNodeManuDetails(
      WellDropScoresView.map((scores: ScoreDefinitionDTO) => {
        const isActiveScore = isScoreActive(
          scores.wellDropScoreId,
          scores.wellDropScoreGroupId,
        );
        return {
          active: isActiveScore,
          color: scores.color,
          hotKey: scores.hotKey,
          onClick: () =>
            scoreWellDrop(scores.wellDropScoreId, scores.wellDropScoreGroupId),
          title: scores.scoreName,
          scoreGroupName: scores.scoreGroupName,
        };
      }),
    );
  }, [
    WellDropScoresView,
    selectedWellsScores,
    selectedWellDetails,
    autoSelectNextWell,
    dropNumber,
    imageBatchId,
    cpId,
  ]);

  function compare(a, b) {
    // Use toUpperCase() to ignore character casing
    const scoreA = a.scoreGroupsId;
    const scoreB = b.scoreGroupsId;

    let comparison = 0;
    if (scoreA > scoreB) {
      comparison = 1;
    } else if (scoreA < scoreB) {
      comparison = -1;
    }
    return comparison;
  }

  function compareHotKey(a, b) {
    // Use toUpperCase() to ignore character casing
    const scoreA = a.hotKey;
    const scoreB = b.hotKey;

    let comparison = 0;
    if (scoreA > scoreB) {
      comparison = 1;
    } else if (scoreA < scoreB) {
      comparison = -1;
    }
    return comparison;
  }

  function createScoreCards(groupScoreList) {
    const sortedGroupScoreList = [...groupScoreList].sort(compareHotKey);
    return (
      <ul className="scores-list">
        {sortedGroupScoreList.map((nodeMenuDetail, index) => (
          <TreeCommandCard
            key={`${nodeMenuDetail.title}-${nodeMenuDetail.hotKey}-${index}`}
            onClick={nodeMenuDetail.onClick}
            iconName={nodeMenuDetail.iconName}
            title={nodeMenuDetail.title}
            active={nodeMenuDetail.active}
            customStyle={customBoxStyle}
            customTextStyle={customTextStyle}
          >
            <KeyStroke keystroke={nodeMenuDetail.hotKey} color={nodeMenuDetail.color} />
          </TreeCommandCard>
        ))}
      </ul>
    );
  }

  function createManualScoresNodes() {
    const tempManualScoresGroups: any[] = [
      ...scoresOptionsList?.filter((scores) => scores.isAutoScore === false),
    ];
    tempManualScoresGroups.sort(compare);

    const interestingGroup = tempManualScoresGroups.find(
      (scoresGroup) => scoresGroup.name === EScoreGroupName.Interesting,
    );
    const otherGroups = tempManualScoresGroups.filter(
      (scoresGroup) => scoresGroup.name !== EScoreGroupName.Interesting,
    );
    const orderedGroups = interestingGroup
      ? [interestingGroup, ...otherGroups]
      : otherGroups;

    return orderedGroups.map((scoresGroup) => {
      const groupScoreList = nodeMenuDetails.filter(
        (scores) => scores.scoreGroupName === scoresGroup.name,
      );
      return (
        <SideBarComponent
          key={`${scoresGroup.name}-scores`}
          heading={scoresGroup.name}
          isCollapsible={true}
          handleColapse={() => {}}
          ContainerComponent={createScoreCards(groupScoreList)}
        />
      );
    });
  }

  function toggleAutoSelectNextWell() {
    dispatch(setAutoSelectNextWell(!autoSelectNextWell));
  }
  if (!firstWell || !scoresOptionsList) {
    return;
  }
  return (
    <div
      id="commandpanel-exp-defaultscore-wrapper"
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <div
        id="commandpanel-exp-defaultscore-experimentpanel"
        style={{ height: '90%', overflow: 'auto' }}
      >
        <div className="flex flex-col px-[10px] gap-x-1 mb-2">
          <div className="font-semibold">Score Options</div>
          <div className="ml-2">
            <div className="flex flex-row items-center">
              <div className="w-[30%] text-sm">Auto Select Next Well</div>
              <FmlxSwitch
                id={'auto-select-next-well'}
                size="sm"
                checked={autoSelectNextWell}
                onChange={toggleAutoSelectNextWell}
              />
            </div>

            <div className="flex flex-row items-center">
              <div className="w-[30%] text-sm">Canvas Score</div>
              <RMMultiSelectSearchDropdown
                id="score-filter"
                options={allScoresOptionsList}
                selectedIds={selectedScoreGroupIds}
                onSelectionChange={handleScoreGroupSelectionChange}
                getOptionId={(item) => item.scoreGroupsId}
                getOptionLabel={(item) => item.name}
                getOptionGroup={(item) =>
                  item.isAutoScore ? 'Auto Scores' : 'Manual Scores'
                }
                enableSearch={false}
                enableLogic={false}
                enableSelectAll={true}
                selectAllLabel="Select All"
                className="w-[70%]"
              />
            </div>
          </div>
        </div>

        <div className="overflow-y-auto h-[58vh]">{createManualScoresNodes()}</div>
      </div>
    </div>
  );
}

export default React.memo(DefaultScores);
